/**
 * Auth return-to navigation for PickDengu.
 *
 * - Explicit /login?returnTo=/some-page is the primary handoff.
 * - React Router state.from is supported for other protected actions.
 * - Existing checkout-resume handoff is supported automatically.
 * - sessionStorage retains an intentional handoff during a Firebase redirect/reload.
 * - Merely visiting /login without an intentional handoff goes to /dashboard.
 * - Never use last-visited localStorage as an implicit login destination.
 */
export const DEFAULT_AFTER_LOGIN_PATH = "/dashboard";
const STORAGE_KEY = "pickdengu:auth:return-to:v1";
const CHECKOUT_PENDING_KEY = "pickdengu:checkout-resume:v1:pending-login";
const CHECKOUT_DRAFT_PREFIX = "pickdengu:checkout-resume:v1:draft:";
const TTL_MS = 30 * 60 * 1000;

type StoredReturn = {
  version: 1;
  path: string;
  expiresAt: number;
};

type PendingCheckout = {
  version: number;
  kind: string;
  path: string;
  expiresAt: number;
};

type RouterFrom = {
  pathname?: string;
  search?: string;
  hash?: string;
};

const getStorage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

/** Prevent external/open redirects and auth-page redirect loops. */
export const safeInternalReturnPath = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (
    !raw.startsWith("/") ||
    raw.startsWith("//") ||
    raw.includes("\\") ||
    /[\u0000-\u001f\u007f]/.test(raw) ||
    /%(?:0[ad]|2f|5c)/i.test(raw)
  ) {
    return null;
  }

  try {
    // new URL normalizes ./../ segments but must remain on our own origin.
    const origin = typeof window === "undefined" ? "https://pickdengu.mw" : window.location.origin;
    const parsed = new URL(raw, origin);
    if (parsed.origin !== origin) return null;
    if (/^\/(?:login|logout|sign-?in|sign-?out|register|signup|auth)(?:\/|$)/i.test(parsed.pathname)) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

const getStateFrom = (state: unknown): string | null => {
  if (!state || typeof state !== "object" || !("from" in state)) return null;
  const from = (state as { from?: unknown }).from;
  if (typeof from === "string") return safeInternalReturnPath(from);
  if (!from || typeof from !== "object") return null;
  const value = from as RouterFrom;
  return safeInternalReturnPath(`${value.pathname || ""}${value.search || ""}${value.hash || ""}`);
};

const readPendingCheckout = (): PendingCheckout | null => {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(CHECKOUT_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckout;
    if (
      parsed.version !== 1 ||
      typeof parsed.kind !== "string" ||
      typeof parsed.expiresAt !== "number" ||
      parsed.expiresAt <= Date.now() ||
      !safeInternalReturnPath(parsed.path)
    ) {
      storage.removeItem(CHECKOUT_PENDING_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const readStoredReturn = (): string | null => {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredReturn;
    const path = safeInternalReturnPath(stored.path);
    if (stored.version === 1 && stored.expiresAt > Date.now() && path) {
      return path;
    }
    storage.removeItem(STORAGE_KEY);
  } catch {
    try { storage.removeItem(STORAGE_KEY); } catch { /* unavailable */ }
  }
  return null;
};

const readExplicitReturn = (search: string, state?: unknown): string | null => {
  try {
    const queryReturn = new URLSearchParams(search).get("returnTo");
    const fromQuery = safeInternalReturnPath(queryReturn);
    if (fromQuery) return fromQuery;
  } catch { /* malformed query */ }
  return getStateFrom(state);
};

/** The destination is only meaningful when sign-in was required. */
export function peekLoginReturnTo(search = "", state?: unknown): string | null {
  return (
    readExplicitReturn(search, state) ||
    (readPendingCheckout() ? safeInternalReturnPath(readPendingCheckout()?.path) : null) ||
    readStoredReturn()
  );
}

/** Call on the login page to survive an OAuth redirect or tab refresh. */
export function rememberLoginReturnTo(search = "", state?: unknown): string | null {
  const path = peekLoginReturnTo(search, state);
  if (!path) return null;
  const storage = getStorage();
  if (storage) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify({
        version: 1,
        path,
        expiresAt: Date.now() + TTL_MS,
      } satisfies StoredReturn));
    } catch { /* storage unavailable */ }
  }
  return path;
}

export function clearLoginReturnTo(): void {
  try { getStorage()?.removeItem(STORAGE_KEY); } catch { /* unavailable */ }
}

/** Resolve and consume only the auth redirect; checkout draft cleanup is independent. */
export function consumeLoginDestination(search = "", state?: unknown): string {
  const destination = peekLoginReturnTo(search, state) || DEFAULT_AFTER_LOGIN_PATH;
  clearLoginReturnTo();
  return destination;
}

/** Reuse the route-state snapshot stored by the existing checkout-resume package. */
export function getCheckoutReturnState(destination: string): unknown {
  const pending = readPendingCheckout();
  const storage = getStorage();
  if (!pending || !storage || pending.path !== destination) return undefined;
  const key = `${CHECKOUT_DRAFT_PREFIX}${pending.kind}:${encodeURIComponent(destination.split("?")[0].split("#")[0])}`;
  try {
    const raw = storage.getItem(key);
    if (!raw) return undefined;
    const saved = JSON.parse(raw) as { version?: number; expiresAt?: number; state?: unknown };
    if (saved.version === 1 && Number(saved.expiresAt) > Date.now()) {
      return saved.state;
    }
  } catch { /* CheckoutResumeNavigator can still restore it. */ }
  return undefined;
}

/** For any future protected action: navigate(buildLoginPath(currentUrl)). */
export function buildLoginPath(returnTo: string): string {
  const path = safeInternalReturnPath(returnTo);
  return path ? `/login?returnTo=${encodeURIComponent(path)}` : "/login";
}
